import React, { useCallback } from 'react';
import { connect } from 'react-redux';
import { change, reduxForm, clearSubmitErrors } from 'redux-form';
import { createStructuredSelector } from 'reselect';
import PropTypes from 'prop-types';
import { OrderGroupFormView } from './order-group-form-view';
import {
	getTypesSelector,
	getIsTypesLoadingSelector,
	getAddressesOptionsSelector,
	getAddressesSearchValueSelector,
	getIsAddressesLoadingSelector,
	getPeriodsOptionsSelector,
	getIsPeriodsLoadingSelector,
	getOrderGroupFormValuesSelector,
	getOrderGroupFormInitialValuesSelector,
	getIsRegularSelector,
	getIsModalShownSelector,
	getIsEditFormShownSelector,
	getConfigsSelector,
	getIsConfigsLoadingSelector,
	getIsTransportTypesLoadingSelector,
	getTransportTypesSelector,
	getModesOptionsSelector,
	getIsModesLoadingSelector,
	getModesSelector,
	getTariffTypesSelector,
	getIsTariffTypesLoadingSelector,
	getAutoparksSelector,
	getIsAutoparksLoadingSelector,
} from '../../selectors';
import {
	getAddressesAction,
	getTypesAction,
	getModesAction,
	setAddressesAction,
	setAddressesSearchValueAction,
	getPeriodsAction,
	createOrderGroupAction,
	getConfigsAction,
	getTransportTypesAction,
	getTariffTypesAction,
	getAutoparksAction,
} from '../../actions';
import { validate } from '../../utils';
import { FORM_NAME } from '../../constants';

const OrderGroupFormWrapper = props => {
	const {
		typesOptions,
		isTypesLoading,
		modesOptions,
		modes,
		isModesLoading,
		periodsOptions,
		isPeriodsLoading,
		addressesOptions,
		addressesSearchValue,
		isAddressesLoading,
		formValues,
		onGetAddresses,
		onSetAddressesSearchValue,
		onFormFieldChange,
		isRegular,
		initialValues,
		isModalShown,
		configsOptions,
		isConfigsLoading,
		transportTypesOptions,
		isTransportTypesLoading,
		tariffTypesOptions,
		isTariffTypesLoading,
		autoparksOptions,
		isAutoparksLoading,
	} = props;

	if (!isModalShown) return null;

	const handleAddressSearchChange = useCallback(
		(event, name) => {
			const value = event.target.value;
			onFormFieldChange(FORM_NAME, name, value);
			onGetAddresses({ searchValue: value, name });
			onSetAddressesSearchValue({ ...addressesSearchValue, [name]: value });
		},
		[onFormFieldChange, onGetAddresses, onSetAddressesSearchValue, addressesSearchValue],
	);

	const handleAddressChange = useCallback(
		(value, name) => {
			onFormFieldChange(FORM_NAME, name, value);
			onSetAddressesSearchValue({ ...addressesSearchValue, [name]: value });
		},
		[onFormFieldChange, onSetAddressesSearchValue, addressesSearchValue],
	);

	const handlePeriodChange = useCallback(
		value => {
			onFormFieldChange(FORM_NAME, 'period_id', value);
		},
		[onFormFieldChange],
	);

	const handleModeChange = useCallback(
		(value, name) => {
			onFormFieldChange(FORM_NAME, name, value);
		},
		[onFormFieldChange],
	);

	const handleRemoveAddressFrom = useCallback(
		index => {
			onSetAddressesSearchValue({
				...addressesSearchValue,
				[`address_from[${index}].address`]: '',
			});
		},
		[onSetAddressesSearchValue, addressesSearchValue],
	);

	const handleRemoveAddressTo = useCallback(
		index => {
			onSetAddressesSearchValue({
				...addressesSearchValue,
				[`address_to[${index}].address`]: '',
			});
		},
		[onSetAddressesSearchValue, addressesSearchValue],
	);

	const handleCheckboxChange = (field, value) => {
		if (field === 'is_pickup_day_to_day') {
			onFormFieldChange(FORM_NAME, 'is_flexible_date', false);
			onFormFieldChange(FORM_NAME, 'is_weekend_pickup', value);
			onFormFieldChange(FORM_NAME, 'is_weekend_drop', true);
		}
		if (field === 'is_flexible_date') {
			onFormFieldChange(FORM_NAME, 'is_pickup_day_to_day', false);
			onFormFieldChange(FORM_NAME, 'is_weekend_drop', !value);
			onFormFieldChange(FORM_NAME, 'is_weekend_pickup', false);
		}
	};

	const handleCreatedDateChange = value => {
		if (value === false) {
			onFormFieldChange(FORM_NAME, 'build_days', null);
		} else if (value === true) {
			onFormFieldChange(FORM_NAME, 'is_start_date', null);
			onFormFieldChange(FORM_NAME, 'start_date', null);
		}
	};

	const handleStartDateChange = value => {
		if (value === false) {
			onFormFieldChange(FORM_NAME, 'start_date', null);
		} else if (value === true) {
			onFormFieldChange(FORM_NAME, 'is_created_date', null);
			onFormFieldChange(FORM_NAME, 'build_days', null);
		}
	};

	const handleCompatibilityAdd = () => {
		const currentCompatibilities = formValues.compatibilities || [];

		const newCompatibilities = [...currentCompatibilities, {}];

		onFormFieldChange(FORM_NAME, 'compatibilities', newCompatibilities);
	};

	const handleAllowWayChange = value => {
		if (value === false) {
			onFormFieldChange(FORM_NAME, 'penalty_cost', null);
		}
	};

	return (
		<OrderGroupFormView
			typesOptions={typesOptions}
			isTypesLoading={isTypesLoading}
			modesOptions={modesOptions}
			modes={modes}
			isModesLoading={isModesLoading}
			periodsOptions={periodsOptions}
			isPeriodsLoading={isPeriodsLoading}
			addressesOptions={addressesOptions}
			addressesSearchValue={addressesSearchValue}
			isAddressesLoading={isAddressesLoading}
			formValues={formValues}
			initialValues={initialValues}
			onAddressSearchChange={handleAddressSearchChange}
			onAddressChange={handleAddressChange}
			onPeriodChange={handlePeriodChange}
			onModeChange={handleModeChange}
			isRegular={isRegular}
			onRemoveAddressFrom={handleRemoveAddressFrom}
			onRemoveAddressTo={handleRemoveAddressTo}
			configsOptions={configsOptions}
			isConfigsLoading={isConfigsLoading}
			transportTypesOptions={transportTypesOptions}
			isTransportTypesLoading={isTransportTypesLoading}
			tariffTypesOptions={tariffTypesOptions}
			isTariffTypesLoading={isTariffTypesLoading}
			autoparksOptions={autoparksOptions}
			isAutoparksLoading={isAutoparksLoading}
			onCheckboxChange={handleCheckboxChange}
			onCreatedDateChange={handleCreatedDateChange}
			onStartDateChange={handleStartDateChange}
			onCompatibilityAdd={handleCompatibilityAdd}
			onAllowWayChange={handleAllowWayChange}
		/>
	);
};

const mapStateToProps = createStructuredSelector({
	typesOptions: getTypesSelector(),
	isTypesLoading: getIsTypesLoadingSelector(),
	modesOptions: getModesOptionsSelector(),
	modes: getModesSelector(),
	isModesLoading: getIsModesLoadingSelector(),
	periodsOptions: getPeriodsOptionsSelector(),
	isPeriodsLoading: getIsPeriodsLoadingSelector(),
	addressesOptions: getAddressesOptionsSelector(),
	addressesSearchValue: getAddressesSearchValueSelector(),
	isAddressesLoading: getIsAddressesLoadingSelector(),
	formValues: getOrderGroupFormValuesSelector(),
	initialValues: getOrderGroupFormInitialValuesSelector(),
	isRegular: getIsRegularSelector(),
	isModalShown: getIsModalShownSelector(),
	isEditFormShown: getIsEditFormShownSelector(),
	configsOptions: getConfigsSelector(),
	isConfigsLoading: getIsConfigsLoadingSelector(),
	transportTypesOptions: getTransportTypesSelector(),
	isTransportTypesLoading: getIsTransportTypesLoadingSelector(),
	tariffTypesOptions: getTariffTypesSelector(),
	isTariffTypesLoading: getIsTariffTypesLoadingSelector(),
	autoparksOptions: getAutoparksSelector(),
	isAutoparksLoading: getIsAutoparksLoadingSelector(),
});

const mapDispatchToProps = {
	onGetTypes: getTypesAction,
	onGetModes: getModesAction,
	onGetPeriods: getPeriodsAction,
	onGetAddresses: getAddressesAction,
	onSetAddresses: setAddressesAction,
	onSetAddressesSearchValue: setAddressesSearchValueAction,
	onFormFieldChange: change,
	onClearSubmitErrors: clearSubmitErrors,
	onCreateOrderGroup: createOrderGroupAction,
	onGetConfigs: getConfigsAction,
	onGetTransportTypes: getTransportTypesAction,
	onGetTariffTypes: getTariffTypesAction,
	onGetAutoparks: getAutoparksAction,
};

OrderGroupFormWrapper.propTypes = {
	typesOptions: PropTypes.array.isRequired,
	isTypesLoading: PropTypes.bool,
	modesOptions: PropTypes.array.isRequired,
	modes: PropTypes.array.isRequired,
	isModesLoading: PropTypes.bool,
	periodsOptions: PropTypes.array.isRequired,
	isPeriodsLoading: PropTypes.bool.isRequired,
	addressesOptions: PropTypes.object.isRequired,
	addressesSearchValue: PropTypes.object.isRequired,
	isAddressesLoading: PropTypes.object.isRequired,
	formValues: PropTypes.object.isRequired,
	initialValues: PropTypes.object,
	onGetTypes: PropTypes.func.isRequired,
	onGetModes: PropTypes.func.isRequired,
	onGetPeriods: PropTypes.func.isRequired,
	onGetAddresses: PropTypes.func.isRequired,
	onSetAddresses: PropTypes.func.isRequired,
	onSetAddressesSearchValue: PropTypes.func.isRequired,
	onFormFieldChange: PropTypes.func.isRequired,
	onClearSubmitErrors: PropTypes.func.isRequired,
	onCreateOrderGroup: PropTypes.func.isRequired,
	handleSubmit: PropTypes.func.isRequired,
	isRegular: PropTypes.bool.isRequired,
	isModalShown: PropTypes.bool.isRequired,
	isEditFormShown: PropTypes.bool.isRequired,
	configsOptions: PropTypes.array.isRequired,
	isConfigsLoading: PropTypes.bool,
	onGetConfigs: PropTypes.func.isRequired,
	onGetTransportTypes: PropTypes.func.isRequired,
	transportTypesOptions: PropTypes.array.isRequired,
	isTransportTypesLoading: PropTypes.bool,
	onGetTariffTypes: PropTypes.func.isRequired,
	tariffTypesOptions: PropTypes.array.isRequired,
	isTariffTypesLoading: PropTypes.bool,
	onGetAutoparks: PropTypes.func.isRequired,
	autoparksOptions: PropTypes.array.isRequired,
	isAutoparksLoading: PropTypes.bool,
};

const withForm = reduxForm({
	form: FORM_NAME,
	enableReinitialize: true,
	onSubmit: validate,
})(OrderGroupFormWrapper);

export const OrderGroupForm = connect(mapStateToProps, mapDispatchToProps)(withForm);
